import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ecptSettingsService } from '../../services';
import { useCurrentUserId } from '../../auth/utils/user';
import type { EcptView, SeriesItem, SettingsViewsDropdowns } from '../../types/api';

export interface ViewRow {
  id: string; // UUID from API
  name: string;
  plant: string;
  shortId?: string | null;
  seriesIds: number[];
  series: string;
  seriesList?: SeriesItem[];
  pemFrom: string;
  pemTo: string;
  columns: string; // comma-separated column display names
  isDefault: boolean;
  detailsExpandedByDefault: boolean;
}

interface EcptSettingsContextType {
  viewRows: ViewRow[];
  setViewRows: React.Dispatch<React.SetStateAction<ViewRow[]>>;
  dropdowns: SettingsViewsDropdowns;
  loading: boolean;
  error: string | null;
  refreshViews: () => void;
}

function mapViewToRow(v: EcptView): ViewRow {
  const seriesList = Array.isArray(v.series) ? v.series
    : v.series_id != null ? [{ series_id: v.series_id, series_name: v.series_name ?? '' }] : [];
  const seriesIds = seriesList.map(s => s.series_id);
  const series = seriesList.map(s => s.series_name).join(', ');

  return {
    id: v.id,
    name: v.view_name,
    plant: v.plant_code,
    shortId: v.short_id ?? null,
    seriesIds,
    series,
    seriesList,
    pemFrom: v.pem_from,
    pemTo: v.pem_to,
    columns: v.columns.map(c => c.column_name).join(', '),
    isDefault: v.is_default,
    detailsExpandedByDefault: v.details_expanded_by_default ?? false,
  };
}

const EMPTY_DROPDOWNS: SettingsViewsDropdowns = { plants: [], series: [], ecpt_columns: [] };

const EcptSettingsContext = createContext<EcptSettingsContextType>({
  viewRows: [],
  setViewRows: () => {},
  dropdowns: EMPTY_DROPDOWNS,
  loading: false,
  error: null,
  refreshViews: () => {},
});

export const useEcptSettings = () => useContext(EcptSettingsContext);

export const EcptSettingsProvider = ({ children }: { children: ReactNode }) => {
  // Subscribe to Redux so the context re-fetches whenever the session changes
  const currentUserId = useCurrentUserId();
  const [viewRows, setViewRows] = useState<ViewRow[]>([]);
  const [dropdowns, setDropdowns] = useState<SettingsViewsDropdowns>(EMPTY_DROPDOWNS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!currentUserId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    ecptSettingsService
      .getViews({ created_by: currentUserId })
      .then((res) => {
        if (!cancelled) {
          setViewRows(res.views.map(mapViewToRow));
          setDropdowns(res.dropdowns);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick, currentUserId]);

  const refreshViews = useCallback(() => {
    setTick(t => t + 1);
  }, []);

  return (
    <EcptSettingsContext.Provider
      value={{ viewRows, setViewRows, dropdowns, loading, error, refreshViews }}
    >
      {children}
    </EcptSettingsContext.Provider>
  );
};
